import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Modal, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { pw9201Api } from '../../services/pw9201Api';
import { usePW9201Mode } from './hooks/usePW9201Mode';
import { usePW9201Record } from './hooks/usePW9201Record';
import { usePW9201Navigation } from './hooks/usePW9201Navigation';
import { usePW9201Lookup } from './hooks/usePW9201Lookup';
import { useModulePermission } from '../../hooks/usePermission';
import { Modules } from '../../constants/permissions';
import PermissionGuard from '../../components/PermissionGuard';
import ActionBar from './components/ActionBar';
import MasterInfo from './components/MasterInfo';
import DetailGrid from './components/DetailGrid';
import './PW9201Form.css';

const PW9201Form: React.FC = () => {
  const navigate = useNavigate();
  const perms = useModulePermission(Modules.PW9201);

  // Hooks
  const { mode, setMode, isEditing } = usePW9201Mode();
  const { names, resolveSingleName, resolveAllNames, clearNames } = usePW9201Lookup();
  const {
    record, formData, details, loading,
    loadRecord, setField, getField,
    updateDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    getFieldError, clearFieldErrors,
  } = usePW9201Record();

  // Wrap loadRecord to also resolve names
  const loadRecordWithNames = useCallback(async (id: string) => {
    const result = await loadRecord(id);
    if (result) {
      resolveAllNames(result);
    }
  }, [loadRecord, resolveAllNames]);

  const { allIds, currentIdx, setCurrentIdx, loadList, navTo } = usePW9201Navigation(loadRecordWithNames);

  // Initial load
  useEffect(() => {
    (async () => {
      try {
        const ids = await loadList();
        if (ids.length > 0) {
          setCurrentIdx(0);
          await loadRecordWithNames(ids[0]);
        }
      } catch {
        // ignore
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handlers
  const handleAdd = useCallback(() => {
    if (isEditing) {
      handleSave(mode, (result) => {
        applyResult(result);
        setMode('browse');
        loadList().then(ids => {
          const idx = ids.indexOf(result.f90b_01);
          if (idx >= 0) setCurrentIdx(idx);
        });
      });
    } else {
      clearNames();
      clearFieldErrors();
      resetForAdd();
      setMode('add');
    }
  }, [isEditing, mode, handleSave, applyResult, setMode, clearNames, clearFieldErrors, resetForAdd, loadList, setCurrentIdx]);

  const handleModify = useCallback(() => {
    if (isEditing) {
      revertToRecord();
      setMode('browse');
    } else {
      if (!record) return;
      setMode('edit');
    }
  }, [isEditing, record, revertToRecord, setMode]);

  const handleDeleteRecord = useCallback(() => {
    if (!record) return;
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除申请单「${record.f90b_01}」？所有明细将一并删除。`,
      okText: '删除', okType: 'danger', cancelText: '取消',
      onOk: async () => {
        const ok = await handleDelete();
        if (ok) {
          const ids = await loadList();
          if (ids.length > 0) {
            const newIdx = Math.min(currentIdx, ids.length - 1);
            setCurrentIdx(newIdx);
            await loadRecordWithNames(ids[newIdx]);
          } else {
            setCurrentIdx(-1);
            clearNames();
            resetForAdd();
          }
        }
      },
    });
  }, [record, handleDelete, loadList, currentIdx, setCurrentIdx, loadRecordWithNames, clearNames, resetForAdd]);

  const handleNavTo = useCallback((idx: number) => {
    navTo(idx, isEditing);
  }, [navTo, isEditing]);

  const handleSearch = useCallback(async (keyword: string) => {
    if (!keyword.trim()) return;
    try {
      const res = await pw9201Api.search(keyword.trim());
      if ((res.data || []).length === 0) {
        message.info('查无资料');
        return;
      }
      const ids = (res.data || []).map(r => r.f90b_01);
      setCurrentIdx(0);
      await loadRecordWithNames(ids[0]);
    } catch {
      message.error('查询失败');
    }
  }, [setCurrentIdx, loadRecordWithNames]);

  // Search state
  const [searchText, setSearchText] = React.useState('');

  return (
    <PermissionGuard module={Modules.PW9201} action="query">
      <Spin spinning={loading}>
        <div className="pw9201-form">
          <div className="page-wrapper">
            {/* Title */}
            <div className="page-title-bar">
              <h1>人员增补申请单</h1>
              <span className="nav-info">
                {allIds.length > 0 ? `${currentIdx + 1} / ${allIds.length}` : '无资料'}
              </span>
            </div>

            {/* Action Bar */}
            <ActionBar
              mode={mode}
              isEditing={isEditing}
              hasRecord={!!record}
              currentIdx={currentIdx}
              allIdsLength={allIds.length}
              permissions={perms}
              onAdd={handleAdd}
              onModify={handleModify}
              onDelete={handleDeleteRecord}
              onNavTo={handleNavTo}
              onExit={() => navigate('/erp')}
            />

            {/* Search */}
            <div className="search-bar">
              <Input
                prefix={<SearchOutlined />}
                placeholder="搜寻申请单号/部门/申请人..."
                allowClear
                style={{ width: 280 }}
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                onPressEnter={() => handleSearch(searchText)}
              />
              <button className="btn" onClick={() => handleSearch(searchText)}>搜寻</button>
            </div>

            {/* Form Content */}
            <div className="form-content">
              <MasterInfo
                formData={formData}
                names={names}
                isEditing={isEditing}
                getField={getField}
                setField={setField}
                resolveSingleName={resolveSingleName}
                getFieldError={getFieldError}
                mode={mode}
              />

              <div className="section-divider">增补明细</div>

              <DetailGrid
                details={details}
                isEditing={isEditing}
                updateDetailField={updateDetailField}
                onAdd={addDetailRow}
                onDelete={deleteDetailRow}
              />

              {/* Page2: Approval section (read-only) */}
              {mode === 'browse' && record && (
                <>
                  <div className="section-divider">审核签核</div>
                  <div className="pw9201-approval-section">
                    <div className="pw9201-approval-block">
                      <h4>人事主管</h4>
                      <div className="pw9201-field-row">
                        <div className="pw9201-field-group">
                          <label>人事主管</label>
                          <Input style={{ width: 120 }} readOnly value={getField('f90b_67') as string || ''} />
                        </div>
                        <div className="pw9201-field-group">
                          <label>知會日期</label>
                          <Input style={{ width: 140 }} readOnly value={formData.f90b_68 ? String(formData.f90b_68).substring(0, 10) : ''} />
                        </div>
                      </div>
                      <div className="pw9201-field-group">
                        <label>說明</label>
                        <Input.TextArea style={{ width: 400 }} rows={2} readOnly value={getField('f90b_69') as string || ''} />
                      </div>
                    </div>
                    <div className="pw9201-approval-block">
                      <h4>副總</h4>
                      <div className="pw9201-field-row">
                        <div className="pw9201-field-group">
                          <label>副總</label>
                          <Input style={{ width: 120 }} readOnly value={getField('f90b_72') as string || ''} />
                        </div>
                        <div className="pw9201-field-group">
                          <label>核准日期</label>
                          <Input style={{ width: 140 }} readOnly value={formData.f90b_73 ? String(formData.f90b_73).substring(0, 10) : ''} />
                        </div>
                      </div>
                      <div className="pw9201-field-group">
                        <label>說明</label>
                        <Input.TextArea style={{ width: 400 }} rows={2} readOnly value={getField('f90b_74') as string || ''} />
                      </div>
                    </div>
                    <div className="pw9201-approval-block">
                      <h4>總經理</h4>
                      <div className="pw9201-field-row">
                        <div className="pw9201-field-group">
                          <label>總經理</label>
                          <Input style={{ width: 120 }} readOnly value={getField('f90b_77') as string || ''} />
                        </div>
                        <div className="pw9201-field-group">
                          <label>核准日期</label>
                          <Input style={{ width: 140 }} readOnly value={formData.f90b_78 ? String(formData.f90b_78).substring(0, 10) : ''} />
                        </div>
                      </div>
                      <div className="pw9201-field-group">
                        <label>說明</label>
                        <Input.TextArea style={{ width: 400 }} rows={2} readOnly value={getField('f90b_79') as string || ''} />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </Spin>
    </PermissionGuard>
  );
};

export default PW9201Form;
